'use client'

import toast from 'react-hot-toast'
import { useIsLoggedInQuery } from 'auth/hooks'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import {
  useStudioAddFavoriteTemplateMutation,
  useStudioIsTemplateFavoritedQuery,
  useStudioRemoveFavoriteTemplateMutation,
} from 'codegen/generated/templates'
import type { StudioIsTemplateFavoritedQuery } from 'codegen/mocks'
import { algoliaAnalytics, AlgoliaEventName } from 'algolia/algolia'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { StudioButton, StudioIconButton } from '../../studio-button'
import {
  CheckIcon,
  ChevronRightIcon,
  Edit05Icon,
  HeartFilledIcon,
  HeartIcon,
  Link03Icon,
  SpinnerIcon,
  XCloseIcon,
  // oxlint-disable-next-line cf-web/banned-imports
} from 'primitives/icons'
import { tw } from 'utils/classnames'
import { localStore } from 'utils/storage'

import { LOCAL_STORAGE_KEY } from '../search-results/handle-local-storage'
import { SearchResultsContainer } from '../search-results'
import type { AlgoliaTemplateHit } from '../context/templates-page'
import { useTemplatesPageContext } from '../context/templates-page'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { Divider } from 'components/divider'
import { ShowMoreText } from 'components/show-more-text'

interface Author {
  avatarUrl?: string | null
  firstName?: string | null
  lastName?: string | null
  slug?: string | null
}

export interface TextsType {
  copyText: string
  copyErrorPrefixText: string
  copyErrorFallbackText: string
  featureTexts: { [key: string]: string }
  byText: string
  customizeText: string
  similarText: string
  goToCategoryText: string
  noResultsText: string
  previousText: string
  nextText: string
  errorText: string
}

const handleCopyToClipboard = async (
  textToCopy: string,
  copyText: string,
  copyErrorPrefixText: string,
  copyErrorFallbackText: string,
) => {
  try {
    await navigator.clipboard.writeText(textToCopy)
    toast.custom(t => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } rounded-4 bg-gray-800-white pointer-events-auto z-[20000] flex h-[72px] w-[321px] items-center justify-center p-4 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1)]`}
      >
        <div className="flex w-0 flex-1">
          <div className="flex flex-1 items-center">
            <div className="rounded-2 text-service-white-white mr-4 flex size-[40px] items-center justify-center bg-violet-500 p-0">
              <Link03Icon className="size-4" />
            </div>
            <div className="flex-1 items-center">
              <p className="text-[15px] font-medium text-gray-50">{copyText}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            toast.remove(t.id)
          }}
          className="rounded-2 text-gray-600-300 hover:text-gray-600-300 active:text-gray-600-300 flex h-[26px] w-[26px] items-center justify-center bg-gray-50 p-0 hover:bg-gray-50 active:bg-gray-50"
        >
          <XCloseIcon className="size-4" />
        </button>
      </div>
    ))
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : copyErrorFallbackText
    toast.error(`${copyErrorPrefixText}: ${errorMessage}`)
  }
}

function setFavorite(queryClient: QueryClient, templateId: string, favorited: boolean) {
  queryClient.setQueryData<StudioIsTemplateFavoritedQuery>(
    useStudioIsTemplateFavoritedQuery.getKey({ templateId }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (!data?.publishedTemplatesFavorited) {
        return data
      }

      return {
        ...data,
        publishedTemplatesFavorited: {
          ...data.publishedTemplatesFavorited,
          favorited,
        },
      }
    },
  )
}

interface TemplateDescriptionProps {
  hit: AlgoliaTemplateHit
  author?: Author | null
  isFavorite?: boolean
  isStudio?: boolean
}
function TemplateDescription({
  hit,
  author,
  isStudio = false,
  isFavorite = false,
  ...texts
}: TemplateDescriptionProps & TextsType) {
  const { studioCreateUrl, Link } = useTemplatesPageContext()
  const queryClient = useQueryClient()
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()

  const addFavoriteMutation = useStudioAddFavoriteTemplateMutation({
    onSuccess({ publishedTemplatesAddFavorite: { errors } }, { templateId }) {
      if (errors) {
        return errors.map(e => toast.error(e.message))
      }

      setFavorite(queryClient, templateId, true)
    },
  })

  const removeFavoriteMutation = useStudioRemoveFavoriteTemplateMutation({
    onSuccess({ publishedTemplatesRemoveFavorite: { errors } }, { templateId }) {
      if (errors) {
        return errors.map(e => toast.error(e.message))
      }

      setFavorite(queryClient, templateId, false)
    },
  })

  const templateId = hit.objectID

  const handleClick = () => {
    //If queryID is present in the hit, use it to send the event
    if (hit.__queryID) {
      algoliaAnalytics('convertedObjectIDsAfterSearch', {
        index: algoliaTemplatesIndex,
        eventName: AlgoliaEventName.PRODUCT_CLICKED,
        queryID: hit.__queryID,
        objectIDs: [templateId],
      })
      return
    }

    try {
      const templatesSearchQueries = localStore.getString(LOCAL_STORAGE_KEY)
      const parsedTemplatesSearchQueries = templatesSearchQueries
        ? JSON.parse(templatesSearchQueries)
        : { hits: {}, queries: {} }

      if (parsedTemplatesSearchQueries.hits[templateId]) {
        algoliaAnalytics('convertedObjectIDsAfterSearch', {
          index: algoliaTemplatesIndex,
          eventName: AlgoliaEventName.PRODUCT_CLICKED,
          queryID: parsedTemplatesSearchQueries.hits[templateId],
          objectIDs: [templateId],
        })
      }
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (er) {
      Sentry.captureException(
        new Error(`Error while trying to access localStorage for ${LOCAL_STORAGE_KEY} 
          error= ${er}`),
      )
    }
  }

  return (
    <div
      className={tw(
        'flex flex-col items-start lg:basis-2/6',
        isStudio
          ? 'w-full lg:basis-3/6 min-[1370px]:basis-2/6'
          : 'md:max-lg:flex-row md:max-lg:justify-between md:max-lg:gap-4',
      )}
    >
      <div className={tw(isStudio && 'flex w-full flex-col items-start justify-center')}>
        <span
          className={tw('typography-14 font-medium text-gray-300', isStudio && 'text-gray-500')}
        >
          {hit.category} <span className={tw(isStudio && 'text-gray-400')}>•</span>{' '}
          {hit.subcategory}
        </span>
        <h2 className="text-24 text-service-white-black mb-2 mt-4 font-semibold [overflow-wrap:anywhere]">
          {hit.title}
        </h2>
        <div className="mb-4 text-gray-100">
          <ShowMoreText
            expandButtonProps={{
              className: tw('text-violet-500', isStudio && 'typography-13'),
            }}
          >
            {hit.subject}
          </ShowMoreText>
        </div>
        <div className="flex items-center">
          {author?.avatarUrl ? (
            <>
              <img
                src={author?.avatarUrl || ''}
                width={32}
                height={32}
                alt="avatar"
                className="mr-2 size-8 rounded-full"
              />
              <span className="typography-14 text-gray-400-400">{texts.byText}</span>
              &nbsp;
              <span className="typography-14 text-violet-500">
                {author?.firstName} {author?.lastName}
              </span>
            </>
          ) : (
            // this means the author is still loading (as it can come from props)
            <div className="h-8 w-8 rounded-full bg-gray-700" />
          )}
        </div>
      </div>
      <div className={tw('shrink-0', isStudio && 'flex w-full flex-col')}>
        <div className="item-start my-4 flex gap-1 sm:items-center">
          <StudioButton
            asChild
            variant={isStudio ? 'violet-aaa' : 'fill'}
            className={tw(isStudio && 'w-full items-center justify-center lg:w-fit')}
          >
            <Link
              onClick={handleClick}
              target="_blank"
              href={`${studioCreateUrl}${templateId}`}
              rel="noreferrer"
            >
              <Edit05Icon className="mr-2 size-6" />{' '}
              <span className={tw(isStudio && 'typography-13')}>{texts.customizeText}</span>
            </Link>
          </StudioButton>
          <StudioIconButton
            variant="gray"
            disabled={addFavoriteMutation.isPending || removeFavoriteMutation.isPending}
            onClick={() => {
              if (!isLoggedInQuery.data) {
                openModal()
                return
              }

              isFavorite
                ? removeFavoriteMutation.mutate({ templateId })
                : addFavoriteMutation.mutate({ templateId })
            }}
          >
            {isFavorite ? <HeartFilledIcon /> : <HeartIcon />}
          </StudioIconButton>
          <StudioIconButton
            variant="gray"
            onClick={() =>
              handleCopyToClipboard(
                (typeof window !== 'undefined' && window.location.href) || '',
                texts.copyText,
                texts.copyErrorPrefixText,
                texts.copyErrorFallbackText,
              )
            }
          >
            <Link03Icon />
          </StudioIconButton>
        </div>
        <div className="typography-12 flex flex-col gap-2 text-gray-50">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div className="flex items-center gap-1" key={i}>
              <CheckIcon className="size-5 text-green-500" />
              <span className="typography-12">{texts.featureTexts[i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export interface TemplateDetailsProps extends TextsType {
  author?: Author | null
  hit: AlgoliaTemplateHit
  isModal?: boolean
}

export function TemplateDetails({ hit, author, isModal = false, ...texts }: TemplateDetailsProps) {
  const { AlgoliaProvider, templatesGalleryRoute, Link } = useTemplatesPageContext()
  const isLoggedInQuery = useIsLoggedInQuery()

  const isFavoritedQuery = useStudioIsTemplateFavoritedQuery(
    { templateId: hit.objectID },
    {
      enabled: Boolean(isLoggedInQuery.data),
      select: data => data.publishedTemplatesFavorited.favorited,
    },
  )

  return (
    <>
      <div className="mb-6 flex min-h-0 min-w-0 flex-col gap-4 lg:flex-row">
        <div className="rounded-4 relative flex h-[586px] flex-shrink justify-center overflow-hidden bg-gray-800 lg:basis-4/6">
          <img
            src={hit.imageUrl}
            width={hit.width}
            height={hit.height}
            alt="Image blurry background"
            className="absolute left-0 top-0 h-full w-full scale-110 opacity-50 blur-xl"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <SpinnerIcon className="size-[50px] animate-spin" />
          </div>
          <img
            key={hit.imageUrl}
            src={hit.imageUrl}
            width={hit.width}
            height={hit.height}
            alt="Template image"
            className="relative h-full w-full object-contain"
          />
        </div>
        <TemplateDescription
          hit={hit}
          author={author}
          isFavorite={Boolean(isFavoritedQuery.data)}
          {...texts}
        />
      </div>
      <div className="flex flex-wrap items-start gap-2">
        {hit.tags.map(tag => (
          <Link
            href={`${templatesGalleryRoute}?tags=${tag}`}
            key={tag}
            className="rounded-4 typography-12 flex h-8 items-center justify-center border border-gray-600 px-4 py-3 font-medium text-gray-100"
          >
            {tag}
          </Link>
        ))}
      </div>
      <Divider className="my-6 bg-gray-700" />
      <div className="mb-6 flex flex-col items-start">
        <div className="flex w-full flex-1 flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <h3 className="text-24 font-semibold">{texts.similarText}</h3>
          <StudioButton
            asChild
            variant="invisible"
            className="typography-13 bg-gray-500-300 h-[34px] font-medium"
          >
            <Link
              href={`${templatesGalleryRoute}${hit.category}${
                hit.subcategory ? `/${hit.subcategory}` : ''
              }/`}
            >
              {texts.goToCategoryText} <ChevronRightIcon />
            </Link>
          </StudioButton>
        </div>
      </div>
      <section className="relative flex min-h-[420px] w-full rounded-[32px]">
        {/* This is here to have different similar results by category */}
        <AlgoliaProvider>
          {isModal ? (
            <SearchResultsContainer hidePagination {...texts} />
          ) : (
            <SearchResultsContainer
              hidePagination
              filters={`category:"${hit.category}"${
                hit.subcategory ? ` AND subcategory:"${hit.subcategory}"` : ''
              }`}
              {...texts}
            />
          )}
        </AlgoliaProvider>
      </section>
    </>
  )
}

export function TemplateDetailsStudio({
  hit,
  author,
  isModal = false,
  ...texts
}: TemplateDetailsProps) {
  const { AlgoliaProvider, templatesGalleryRoute, Link } = useTemplatesPageContext()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  const { data: isFavorite } = useStudioIsTemplateFavoritedQuery(
    { templateId: hit.objectID },
    {
      enabled: Boolean(isLoggedIn),
      select: data => data.publishedTemplatesFavorited.favorited,
    },
  )

  return (
    <>
      <div className="mb-6 flex min-h-0 min-w-0 flex-col justify-center gap-4 lg:flex-row">
        <div className="rounded-4 relative flex h-[586px] w-full max-w-[1276px] flex-shrink basis-3/4 justify-center overflow-hidden bg-gray-800 sm:basis-[unset] lg:basis-3/6 min-[1370px]:basis-4/6">
          <img
            src={hit.imageUrl}
            width={hit.width}
            height={hit.height}
            alt="Image blurry background"
            className="absolute left-0 top-0 h-full w-full scale-110 opacity-50 blur-xl"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <SpinnerIcon className="size-[50px] animate-spin" />
          </div>
          <img
            key={hit.imageUrl}
            src={hit.imageUrl}
            width={hit.width}
            height={hit.height}
            alt="Template image"
            className="relative h-full w-full object-contain"
          />
        </div>
        <TemplateDescription
          isStudio
          hit={hit}
          author={author}
          isFavorite={Boolean(isFavorite)}
          {...texts}
        />
      </div>
      <div className="flex w-full flex-col gap-4">
        {hit.tags?.length ? (
          <span className="font-inter typography-13 text-gray-300 antialiased">Related tags</span>
        ) : null}
        <div className="flex flex-wrap items-start gap-2">
          {hit.tags.map(tag => (
            <Link
              href={`${templatesGalleryRoute}?tags=${tag}`}
              key={tag}
              className="rounded-4 typography-12 font-inter flex h-8 items-center justify-center border border-gray-600 px-4 py-3 font-medium text-gray-100 antialiased"
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
      <Divider className="my-6 bg-gray-900" />
      <div className="mb-6 flex flex-col items-start">
        <div className="flex w-full flex-1 flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <h3 className="text-24 font-semibold">{texts.similarText}</h3>
          <StudioButton
            asChild
            variant="invisible"
            className="typography-14 bg-gray-500-300 font-poppins h-[34px] font-medium"
          >
            <Link
              href={`${templatesGalleryRoute}${hit.category}${
                hit.subcategory ? `/${hit.subcategory}` : ''
              }/`}
            >
              {texts.goToCategoryText} <ChevronRightIcon className="text-cf-black" />
            </Link>
          </StudioButton>
        </div>
      </div>
      <section className="relative flex min-h-[420px] w-full rounded-[32px]">
        {/* This is here to have different similar results by category */}
        <AlgoliaProvider>
          {isModal ? (
            <SearchResultsContainer hidePagination {...texts} />
          ) : (
            <SearchResultsContainer
              hidePagination
              filters={`category:"${hit.category}"${
                hit.subcategory ? ` AND subcategory:"${hit.subcategory}"` : ''
              }`}
              {...texts}
            />
          )}
        </AlgoliaProvider>
      </section>
    </>
  )
}
